import { Box, ButtonBS, HStack, Spinner, StackProps, Text, useBreakpoint, VStack } from "@biblioteksentralen/react";
import { AdventCalendarDoorType } from "@libry-content/types";
import { useEffect, useState } from "react";
import { logEvent } from "../../../utils/analytics/Plausible";
import { TranslateMicroText, useTranslation } from "../../../utils/hooks/useTranslation";
import { useCommonData } from "../../layout/CommonDataProvider";
import { Answer } from "../CalendarDoorModal/types";
import { BsShare } from "./BsShare";
import { PopupLink } from "./PopupLink";
import { Facebook } from "react-feather";
import { adventCalendarSOMEImagePath } from "./adventCalendarSOMEImagePath";

const createUrl = (baseUrl: string, queryParams: Record<string, string> = {}): string => {
  const url = new URL(baseUrl);

  Object.entries(queryParams).forEach(([key, value]) => url.searchParams.append(key, value));

  return url.toString();
};

const getShareApiText = (
  t: TranslateMicroText,
  date: number,
  type?: AdventCalendarDoorType,
  answered?: Answer
): string => {
  switch (type) {
    case "adventCalendarDoorQuizWithAlternatives":
      return answered === "correct"
        ? t("Jeg svarte riktig på luke {dato} i julequizen", { dato: date })
        : t("Jeg deltok i julequizen", { dato: date });
    case "adventCalendarDoorGuessAuthor":
      return t("Jeg klarte utfordringen i luke {dato}", { dato: date });
    case "adventCalendarDoorQuote":
      return t("Det skjulte seg et fint sitat i luke {dato}", { dato: date });
    case "adventCalendarDoorWordSearch":
      return t("Jeg klarte ordjakten i luke {dato}", { dato: date });
    case "adventCalendarDoorVideo":
      return t("Det skjulte seg en video i luke {dato}", { dato: date });
    default:
      return t("Jeg åpnet luke {dato}", { dato: date });
  }
};

type Props = {
  shareUrl: string;
  answered?: Answer;
  type?: AdventCalendarDoorType;
  date: number;
};

export const ShareToSocialMedia = ({ shareUrl, answered, type, date, ...stackProps }: Props & StackProps) => {
  const { t, ts } = useTranslation();
  const { site } = useCommonData();
  const siteName = ts(site?.name);
  const breakpoint = useBreakpoint();

  const shareApiEnabled =
    typeof navigator !== "undefined" &&
    typeof navigator.canShare === "function" &&
    !navigator.userAgent.match(/android/i);
  const shouldUseShareApi = shareApiEnabled && breakpoint && ["base", "sm"].includes(breakpoint);

  const [shareData, setShareData] = useState<ShareData | undefined | "failed">(undefined);
  const loadingShareData = shouldUseShareApi && shareData === undefined;
  const employShareApi = shouldUseShareApi && typeof shareData === "object" && navigator.canShare(shareData);

  useEffect(() => {
    if (!shouldUseShareApi) return;

    fetch(adventCalendarSOMEImagePath)
      .then(async (response) => {
        if (!response.ok) {
          console.error(
            `Could not get image ${response.url} for share data; request failed (${response.status}, "${response.statusText}")`
          );
          setShareData("failed");
          return;
        }

        const blob = await response.blob();
        const extension = adventCalendarSOMEImagePath.split(".").reverse()[0];
        const fileName = `${t("Julekalender")}.${extension}`;
        const file = new File([blob], fileName);

        // field "title" appears to be ignored in most apps
        setShareData({
          url: shareUrl,
          text: getShareApiText(t, date, type, answered),
          files: [file],
        });
      })
      .catch((err) => {
        console.error(`Could not get image ${adventCalendarSOMEImagePath} for share data: "${err.toString()}"`);
        setShareData("failed");
      });
  }, [answered, date, shouldUseShareApi, shareUrl, siteName, t, type]);

  const handleShareApi = () => {
    logEvent("AdventCalendarShare", { doorType: type, target: "share-api", date });

    if (typeof shareData !== "object") {
      console.error("Something went wrong when using the share API");
      return;
    }

    navigator.share(shareData).catch((err: unknown) => {
      const errorHasToStringMethod = typeof err === "object" && typeof err?.toString === "function";

      // This error occurs with normal user actions like closing the share window
      if (errorHasToStringMethod && err.toString().startsWith("AbortError")) return;

      console.error(err);
    });
  };

  if (loadingShareData) {
    return (
      <Box height="2.5rem" width="100%" textAlign="center" {...stackProps}>
        <Spinner />
      </Box>
    );
  }

  if (employShareApi) {
    return (
      <Box width="100%" textAlign="center" {...stackProps}>
        <ButtonBS
          variant="ghost"
          onClick={handleShareApi}
          _hover={{ background: "none" }}
          textAlign="center"
          marginTop="0!important"
          height="3rem"
        >
          <VStack gap="0.25rem">
            <BsShare width="1.2rem" height="1.2rem" />
            <Text fontSize="md" marginTop="0!important">
              {t("Del")}
            </Text>
          </VStack>
        </ButtonBS>
      </Box>
    );
  }

  return (
    <VStack marginTop="0!important" {...stackProps}>
      <Text fontSize="xs" fontWeight={600}>
        {t("Del på Facebook")}
      </Text>
      <VStack>
        <HStack gap="0.5rem">
          <PopupLink
            popupWidth={600}
            background="#1877f2"
            color="white"
            borderRadius="50%"
            width="1.5rem"
            height="1.5rem"
            aria-label={t("Del på {sosialtMedie}", { sosialtMedie: "Facebook" })}
            href={createUrl("http://www.facebook.com/sharer.php", { u: shareUrl })}
            onClick={() => logEvent("AdventCalendarShare", { doorType: type, target: "facebook", date })}
          >
            <Box position="absolute" top="0.25rem">
              <Facebook size="1.25rem" />
            </Box>
          </PopupLink>
        </HStack>
      </VStack>
    </VStack>
  );
};
